import { useAppSelector } from '@dealstack/frontend/app/hooks/redux';
import { GridColumn } from '@dealstack/frontend/app/pages/Valuations/components/GridColumn/GridColumn';
import {
	defaultValuationDetailsFormValues,
	VALUATION_HEADER_CURRENCIES,
} from '@dealstack/frontend/app/pages/Valuations/constants';
import { ValuationWithClassifierRes, ValuationNumberFormat } from '@dealstack/shared/lib/generated';
import { useValuationClassifiers } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationClassifiers';
import { useValuationNavigation } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationNavigation';
import { useValuationsQuery } from '@dealstack/frontend/app/pages/Valuations/queries/useValuationsQuery';
import { FormInputWrapper } from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/styled';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { UTCDateInput } from '@dealstack/frontend/shared/components/mantineForm/UTCDateInput';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import { getUserDealList } from '@dealstack/frontend/shared/dataExchange/services/project.service';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { EARLIEST_FX_DATE } from '@dealstack/shared/lib/common/date/date';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Button, Flex, ComboboxItem, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { find } from 'lodash';
import { useEffect, useState } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { MultiSelect, Select, TextInput } from 'react-hook-form-mantine';
import { ValuationDetailsForm } from '@dealstack/frontend/app/pages/Valuations/constants.types';
import { FormContainer } from './styled';

interface Props {
	onFXRatesClick: () => void;
	currentValuation: ValuationWithClassifierRes | undefined;
	upsertValuation: { save: (values: ValuationDetailsForm) => void; cancel: () => void };
}

export const ValuationDetails = ({ onFXRatesClick, currentValuation, upsertValuation }: Props) => {
	const { valuationClassifiers } = useValuationClassifiers();

	const tValuations = useTranslationValuations();
	const { dealId } = useValuationNavigation();
	const { isLoading: isValuationsListLoading } = useValuationsQuery(dealId);
	const userState = useAppSelector((state) => state.user);
	const getDealListRequest = useQuery({
		queryKey: [API_PATHS.DEAL_GET, userState.data],
		queryFn: ({ signal }) => getUserDealList(signal),
		enabled: !!userState.data?.id,
	});

	const valuationDetailsForm = useFormContext<ValuationDetailsForm>();
	const { control, reset: resetForm, subscribe: subscribeForm } = valuationDetailsForm;

	const valuationTagLabels: ComboboxItem[] = valuationClassifiers.map((classifier) => ({
		id: classifier.id,
		label: tValuations(`classifier.${classifier.id}`),
		value: classifier.id,
	}));

	const VALUATION_MULTIPLES_OPTIONS: { label: string; value: ValuationNumberFormat }[] =
		Object.values(ValuationNumberFormat).map((value) => ({
			label: tValuations(`multiples.${value.toLocaleLowerCase('en-GB')}`),
			value,
		}));

	const [initialized, setInitialized] = useState(false);

	useEffect(() => {
		if (initialized) {
			return;
		}

		if (currentValuation) {
			resetForm({
				valuationTitle: currentValuation.name,
				valuationMultiples: currentValuation.defaultNumberFormat,
				valuationDefaultCurrency: currentValuation.defaultCurrency,
				valuationDate: currentValuation.valuationDate.split('T')[0],
				valuationTags: currentValuation.classifiers.map((classifier) => classifier.id),
			});
			setInitialized(true);
		} else if (!getDealListRequest.isLoading && !isValuationsListLoading) {
			// List is loaded and no matching valuation - this is a fresh row the
			// form is about to upsert. Prime the default currency from the deal.
			const dealDefaultCurrency = find(getDealListRequest.data?.payload, {
				id: dealId,
			})?.currency;

			resetForm({
				...defaultValuationDetailsFormValues,
				valuationDefaultCurrency:
					dealDefaultCurrency ??
					defaultValuationDetailsFormValues.valuationDefaultCurrency,
			});
			setInitialized(true);
		}
	}, [
		currentValuation,
		resetForm,
		getDealListRequest.isLoading,
		getDealListRequest.data,
		dealId,
		isValuationsListLoading,
		initialized,
	]);

	useEffect(() => {
		const unsubscribe = subscribeForm({
			formState: {
				isDirty: true,
				isValid: true,
				values: true,
			},
			callback: ({ values, isDirty, isValid }) => {
				if (isDirty && isValid) {
					upsertValuation.save(values);
				} else {
					upsertValuation.cancel();
				}
			},
		});
		return () => unsubscribe();
	}, [subscribeForm, upsertValuation]);

	return (
		<FormProvider {...valuationDetailsForm}>
			<FormContainer>
				<GridColumn span={5}>
					<TextV2 variant="sans/14/600">
						{tValuations('form_field.valuation_title')} *
					</TextV2>
					<FormInputWrapper>
						<TextInput
							control={control}
							size="sm"
							type="text"
							placeholder={tValuations(
								'form_field.untitled_valuation_title_placeholder'
							)}
							name="valuationTitle"
							data-testid={TEST_ELEMENT_IDS.VALUATION_EDIT_TITLE_INPUT}
						/>
					</FormInputWrapper>
				</GridColumn>
				<GridColumn span={5}>
					<TextV2 variant="sans/14/600">
						{tValuations('form_field.input_amount_in_multiples_of')}
					</TextV2>
					<FormInputWrapper>
						<Select
							control={control}
							size="sm"
							name="valuationMultiples"
							allowDeselect={false}
							data={VALUATION_MULTIPLES_OPTIONS}
							placeholder={tValuations(
								'form_field.input_amount_in_multiples_of_placeholder'
							)}
							rightSection={<Icon variant="caret-down" size="vlg" />}
							rightSectionProps={{
								style: { pointerEvents: 'none' },
							}}
							sx={{
								'input:focus:not([data-invalid])': {
									boxShadow: 'none',
								},
							}}
							data-testid={TEST_ELEMENT_IDS.VALUATION_EDIT_SELECT_FORMATTING}
						/>
					</FormInputWrapper>
				</GridColumn>
				<GridColumn span={2}>
					<TextV2 variant="sans/14/600">
						{tValuations('form_field.default_currency')}
					</TextV2>
					<FormInputWrapper>
						<Select
							control={control}
							size="sm"
							name="valuationDefaultCurrency"
							allowDeselect={false}
							data={VALUATION_HEADER_CURRENCIES.map((currency) => ({
								label: currency,
								value: currency,
							}))}
							placeholder={tValuations('form_field.default_currency_placeholder')}
							rightSection={<Icon variant="caret-down" size="vlg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							sx={{
								'input:focus:not([data-invalid])': {
									boxShadow: 'none',
								},
							}}
							data-testid={TEST_ELEMENT_IDS.VALUATION_EDIT_DEFAULT_CURRENCY_SELECT}
						/>
					</FormInputWrapper>
				</GridColumn>
				<GridColumn span={5}>
					<TextV2 variant="sans/14/600">
						{tValuations('form_field.valuation_date')}
					</TextV2>
					<FormInputWrapper>
						<UTCDateInput
							size="sm"
							name="valuationDate"
							placeholder={tValuations('form_field.valuation_date_placeholder')}
							data-testid={TEST_ELEMENT_IDS.VALUATION_EDIT_DATE_INPUT}
							valueFormat="DD/MM/YYYY"
							leftSection={<Icon variant="calendar-v3" size="vlg" />}
							minDate={EARLIEST_FX_DATE.toJSDate()}
						/>
					</FormInputWrapper>
				</GridColumn>
				<GridColumn span={5}>
					<TextV2 variant="sans/14/600">{tValuations('form_field.tags')}</TextV2>
					<FormInputWrapper>
						<MultiSelect
							size="sm"
							control={control}
							name="valuationTags"
							data={valuationTagLabels}
							placeholder={tValuations('form_field.tags_placeholder')}
							rightSection={<Icon variant="caret-down" size="vlg" />}
							styles={{ rightSection: { pointerEvents: 'none' } }}
							sx={{
								'input:focus:not([data-invalid])': {
									boxShadow: 'none',
								},
							}}
							disabled={!currentValuation}
							data-testid={TEST_ELEMENT_IDS.VALUATION_EDIT_TAGS_SELECT}
						/>
					</FormInputWrapper>
				</GridColumn>
				<GridColumn span={2} style={{ paddingTop: '0.5rem' }}>
					<Button
						onClick={onFXRatesClick}
						variant="default"
						fullWidth
						p="0.75rem"
						h="3.5rem"
						sx={{
							'.mantine-Button-label': { width: '100%' },
						}}
						disabled={!currentValuation}
						data-testid={TEST_ELEMENT_IDS.VALUATION_EDIT_FX_CONVERTER_BUTTON}
					>
						<Flex align="center" justify="space-between" sx={{ width: '100%' }}>
							<Text size="B6Bold" c="dark.9">
								{tValuations('form_field.fx_converter')}
							</Text>
							<Icon variant="coin-exchange" size="xl" />
						</Flex>
					</Button>
				</GridColumn>
			</FormContainer>
		</FormProvider>
	);
};
